<template>
  <div
    class="user-photo-upload"
    :class="{ 'long-press-active': isLongPress }"
    @click.stop
    @contextmenu.prevent
    @dragstart.prevent
    @selectstart.prevent
    @touchstart.stop.prevent="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend.stop.prevent="onTouchEnd"
    @touchcancel="cancelLongPress"
  >
    <file-upload
      ref="upload"
      v-model="files"
      accept="image/*"
      :headers="headers"
      :multiple="false"
      :post-action="uploadUrl"
      :size="maxFileSize"
      @input-file="inputFile"
      @input-filter="inputFilter"
    >
      <slot />
    </file-upload>
  </div>
</template>

<script>
import auth from '@it-enterprise/jwtauthentication/dist/jwtauthentication.umd.min.js'
import fileUpload from 'vue-upload-with-credential'
import { MainMenuApi as api } from '@/mainMenuSrc/api/api.js'
import { notificationApi } from '@/api/notificationApi'

const LONG_PRESS_DURATION = 600
const TOUCH_MOVE_THRESHOLD = 10

export default {
  name: 'UserPhotoUpload',
  components: {
    fileUpload
  },
  data: () => ({
    authToken: '',
    files: [],
    maxFileSize: 100 * 1024 * 1024,
    longPressTimer: null,
    touchStartPosition: null,
    isLongPress: false
  }),
  computed: {
    headers() {
      return {
        'Upload-Type': 'single',
        'Authorization': `Bearer ${this.authToken}`
      }
    },
    uploadUrl() {
      return this.$store.state.general.config.GraphQlUrl + 'upload'
    }
  },
  async mounted() {
    this.authToken = await auth.getToken()
  },
  methods: {
    onTouchStart(event) {
      this.cancelLongPress()
      const touch = event.touches[0]
      this.touchStartPosition = { x: touch.clientX, y: touch.clientY }
      this.longPressTimer = window.setTimeout(() => {
        this.longPressTimer = null
        this.isLongPress = true
        this.openFileDialog()
      }, LONG_PRESS_DURATION)
    },
    onTouchMove(event) {
      if (!this.touchStartPosition) {
        return
      }
      const touch = event.touches[0]
      const moved = Math.abs(touch.clientX - this.touchStartPosition.x) > TOUCH_MOVE_THRESHOLD ||
        Math.abs(touch.clientY - this.touchStartPosition.y) > TOUCH_MOVE_THRESHOLD
      if (moved) {
        this.cancelLongPress()
      }
    },
    onTouchEnd(event) {
      if (this.longPressTimer) {
        this.cancelLongPress()
        this.$emit('short-touch', event)
      } else if (this.isLongPress) {
        this.isLongPress = false
        this.touchStartPosition = null
      }
    },
    cancelLongPress() {
      if (this.longPressTimer) {
        window.clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
      this.touchStartPosition = null
      this.isLongPress = false
    },
    openFileDialog() {
      const input = this.$refs.upload?.$el?.querySelector('input[type="file"]')
      if (!input) {
        return
      }
      input.click()
    },
    inputFilter(newFile, oldFile, prevent) {
      if (!newFile || oldFile) {
        return
      }
      if (!newFile.file?.type?.startsWith('image/')) {
        prevent()
        return
      }
      this.$refs.upload.active = true
    },
    async inputFile(newFile, oldFile) {
      if (newFile?.error) {
        notificationApi.showError({ text: newFile.error, showDetails: false })
        this.files = []
        return
      }
      if (!newFile || !oldFile || newFile.success === oldFile.success || !newFile.success) {
        return
      }

      try {
        const response = await api.editUserPhoto(newFile.response.fileName)
        const photo = response.data.iTEnterpriseMenuMutation.editUserPhoto.photo
        if (!photo) {
          return
        }
        const mimeType = newFile.file.type || 'image/jpeg'
        this.$emit('photo-updated', `data:${mimeType};base64,${photo}`)
      } finally {
        this.files = []
      }
    }
  },
  beforeDestroy() {
    this.cancelLongPress()
  }
}
</script>

<style scoped>
.user-photo-upload {
  display: flex;
  cursor: pointer;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}
</style>
